import { useContext, useEffect, useState } from 'react';

import {
	ArrowLeftOutlined,
	ClockCircleOutlined,
	FileDoneOutlined,
	PlayCircleOutlined,
	StarOutlined,
} from '@ant-design/icons';
import {
	Button,
	Card,
	Descriptions,
	Flex,
	Pagination,
	Spin,
	Statistic,
	Tabs,
	Tooltip,
	Typography,
} from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import {
	useGetCategories,
	useGetSingleCategory,
} from '@/api/monitor_collections';
import { opensearchApi } from '@/api/opensearch_results';
import VideoCard from '@/components/common/VideoCard';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { mockVideos } from '@/data/mockVideos';
import { useOpenSearch } from '@/hooks/useOpenSearch';
import { Video, convertOpenSearchToVideo } from '@/interfaces/video';
import placeholderImg from '@/lib/assets/placeholder.jpg';
import { formatNumber } from '@/lib/helpers/formatNumber';
import { formatSecondsToTime } from '@/lib/helpers/formatSecondsToTime';
import getReadableCron from '@/lib/helpers/getReadableCron';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import { PageContext } from '@/providers/PageContextProvider';

import styles from './styles.module.css';

import FiltersSection from '../../video-insights-co-pilot/FiltersSection';
import Creators from './Creators';
import SidebarCharts from './SidebarCharts';
import QAResults from './components/QAResults';

const UPDATE_FREQUENCY = {
	0: 'Manually',
	1: 'Daily',
	2: 'Weekly',
	3: 'Monthly',
	4: 'Custom',
};

const ENTITIES_TYPES = {
	event: {
		label: 'Event',
		description: 'Named event (e.g. wars, sports events, festivals)',
	},
	fac: {
		label: 'Facility',
		description: '(e.g. buildings, airports, highways, bridges)',
	},
	gpe: {
		label: 'Geopolitical',
		description: '(e.g. countries, cities, states)',
	},
	law: {
		label: 'Law',
		description: 'Named legal documents (e.g. treaties, laws, constitutions)',
	},
	loc: {
		label: 'Location',
		description: 'Non-GPE locations (e.g. mountains, bodies of water)',
	},
	norp: {
		label: 'Nation/Religion/Politics',
		description: 'Nationalities, religious and political groups',
	},
	orgs: {
		label: 'Organisations/Institutions',
		description:
			'Named organizations (e.g. companies, institutions, government bodies)',
	},
	persons: {
		label: 'Persons',
		description: 'Named people (individuals and fictional characters)',
	},
	products: {
		label: 'Products',
		description: 'Named products (e.g. vehicles, phones, software)',
	},
	woa: {
		label: 'Work of Art',
		description: 'Named creative works (e.g. books, songs, paintings)',
	},
} as const;

type TEntityType = keyof typeof ENTITIES_TYPES;

export default function SingleCategoryPage() {
	const { id } = useParams();
	const location = useLocation();
	const { setPageTitle, setIsCreditsCounterVisible } = useContext(PageContext);
	const navigate = useNavigate();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const [isPanelOpen, setIsPanelOpen] = useState(false);
	const [isFiltersApplied, setIsFiltersApplied] = useState(false);
	const [searchResults, setSearchResults] = useState<Video[]>([]);

	const [appliedData, setAppliedData] = useState<any>(null);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [searchEntities, setSearchEntities] = useState<string[]>([]);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;
	const [totalResults, setTotalResults] = useState(0);

	// Summary Content state
	const [summaryContent, setSummaryContent] = useState<string>('');
	const [summaryLoading, setSummaryLoading] = useState(false);
	const [summaryError, setSummaryError] = useState<string>('');

	// Q&A Results state
	const [qaAnswer, setQaAnswer] = useState<string>('');
	const [qaResultsSummary, setQaResultsSummary] = useState<string>('');
	const [qaError, setQaError] = useState<string | null>(null);
	const [isAnswerLoading, setIsAnswerLoading] = useState(false);
	const [isResultsLoading, setIsResultsLoading] = useState(false);
	const [userQuestion, setUserQuestion] = useState<string>('');

	// OpenSearch integration
	const {
		results: opensearchResults,
		loading: opensearchLoading,
		error: opensearchError,
		semanticSearch,
		naturalLanguageSearch,
		hybridSearch,
		clearResults,
		checkHealth,
		isHealthy,
	} = useOpenSearch();

	const {
		data: categoryData,
		isLoading,
		isFetching,
	} = useGetSingleCategory(id ? id : '');

	const item = categoryData?.data?.data?.item;
	const outerEntities = categoryData?.data?.data?.item?.entities;

	// Handle search from filters (legacy - for backward compatibility)
	const handleSearch = async (query: string, page: number = 1) => {
		if (!query.trim()) {
			setSearchResults([]);
			setIsFiltersApplied(false);
			setTotalResults(0);
			setCurrentPage(1);
			return;
		}

		// If this is a new search (different query), reset to page 1
		if (query !== searchTerm) {
			setCurrentPage(1);
			page = 1;
		}

		setSearchTerm(query);
		setIsFiltersApplied(true);

		try {
			// Map category name to correct index name (same as summary generation)
			const categoryToIndexMap: Record<string, string> = {
				'AI Influencers (US)': 'ai-influencers-us-transcripts',
				'Tech influencers (US)': 'tech-influencers-transcripts',
				'Politics (US)': 'politics-us-transcripts',
				'US news': 'us-news-transcripts',
				'Generalist (North America)': 'generalist-north-america-transcripts',
				Skincare: 'skincare-transcripts',
			};

			const indexName =
				categoryToIndexMap[item?.name || ''] || 'ai-influencers-us-transcripts';

			// Calculate offset for pagination
			const from = (page - 1) * itemsPerPage;

			// Use natural-language search with pagination
			await naturalLanguageSearch({
				query: query,
				size: itemsPerPage,
				from: from,
				index_pattern: indexName,
			});
		} catch (error) {
			console.error('Search failed:', error);
		}
	};

	// Handle hybrid search from filters (new two-step workflow)
	const handleHybridSearch = async (
		params: {
			semantic_query: string;
			keyword_query?: string;
			entity_query?: string;
			date_range?: { from_date: string; to_date: string };
			analysis_date_range?: { from_date: string; to_date: string };
			platform?: string;
			language?: string;
			reach?: string;
			sentiment?: string;
			entity_logic?: string;
			original_question?: string; // Add original question for Q&A
		},
		page: number = 1,
	) => {
		if (!params.semantic_query.trim()) {
			setSearchResults([]);
			setIsFiltersApplied(false);
			setTotalResults(0);
			setCurrentPage(1);
			return;
		}

		// Check if this is a new search by comparing with current search parameters
		const isNewSearch =
			params.semantic_query !== searchTerm ||
			JSON.stringify(params.date_range) !==
				JSON.stringify(appliedData?.filter_data?.date_range) ||
			JSON.stringify(params.analysis_date_range) !==
				JSON.stringify(appliedData?.filter_data?.analysis_date_range) ||
			params.platform !== appliedData?.filter_data?.platform ||
			params.language !== appliedData?.filter_data?.language ||
			params.reach !== appliedData?.filter_data?.reach ||
			params.sentiment !== appliedData?.filter_data?.sentiment ||
			params.entity_logic !== appliedData?.filter_data?.entity_logic ||
			params.keyword_query !== appliedData?.filter_data?.keywords?.join(', ') ||
			params.entity_query !== appliedData?.filter_data?.brands?.join(', ');

		// Only reset to page 1 if this is a new search
		if (isNewSearch) {
			setCurrentPage(1);
			page = 1;
		} else {
			// For pagination, update the current page
			setCurrentPage(page);
		}

		setSearchTerm(params.semantic_query);
		setSearchEntities(
			params.entity_query
				? params.entity_query.split(',').map((e) => e.trim())
				: [],
		);

		// 🚀 Start Q&A loading states immediately when search begins
		setIsAnswerLoading(true);
		setIsResultsLoading(true);
		setQaError(null);
		setQaAnswer('');
		setQaResultsSummary('');
		setUserQuestion(params.original_question || params.semantic_query); // Store the original question for Q&A

		setIsFiltersApplied(true);

		// Update appliedData to keep FiltersSection synchronized
		setAppliedData((prevData: any) => ({
			...prevData,
			filter_data: {
				...prevData?.filter_data,
				semantic_query: params.semantic_query,
				keywords: params.keyword_query
					? params.keyword_query.split(',').map((k) => k.trim())
					: [],
				brands: params.entity_query
					? params.entity_query.split(',').map((e) => e.trim())
					: [],
				date_range: params.date_range,
				analysis_date_range: params.analysis_date_range,
				platform: params.platform,
				language: params.language,
				reach: params.reach,
				sentiment: params.sentiment,
				entity_logic: params.entity_logic,
				// Add camelCase versions for FiltersSection compatibility
				analysisDateRange: params.analysis_date_range
					? {
							from: params.analysis_date_range.from_date,
							till: params.analysis_date_range.to_date,
					  }
					: prevData?.filter_data?.analysisDateRange || { from: '', till: '' },
				videoDateRange: params.date_range
					? {
							from: params.date_range.from_date,
							till: params.date_range.to_date,
					  }
					: prevData?.filter_data?.videoDateRange || { from: '', till: '' },
			},
		}));

		try {
			// Map category name to correct index name (same as summary generation)
			const categoryToIndexMap: Record<string, string> = {
				'AI Influencers (US)': 'ai-influencers-us-transcripts',
				'Tech influencers (US)': 'tech-influencers-transcripts',
				'Politics (US)': 'politics-us-transcripts',
				'US news': 'us-news-transcripts',
				'Generalist (North America)': 'generalist-north-america-transcripts',
				Skincare: 'skincare-transcripts',
			};

			const indexName =
				categoryToIndexMap[item?.name || ''] || 'ai-influencers-us-transcripts';

			// Calculate offset for pagination
			const from = (page - 1) * itemsPerPage;

			// Use hybrid search with pagination
			await hybridSearch({
				semantic_query: params.semantic_query,
				keyword_query: params.keyword_query,
				entity_query: params.entity_query,
				size: itemsPerPage,
				from: from,
				index_pattern: indexName,
				from_date: params.date_range?.from_date,
				to_date: params.date_range?.to_date,
				analysis_from_date: params.analysis_date_range?.from_date,
				analysis_to_date: params.analysis_date_range?.to_date,
				platform: params.platform,
				language: params.language,
				reach: params.reach,
				sentiment: params.sentiment,
				entity_logic: params.entity_logic,
			});
		} catch (error) {
			console.error('Hybrid search failed:', error);
		}
	};

	// Convert OpenSearch results to Video format when results change
	useEffect(() => {
		if (opensearchResults?.results) {
			const convertedVideos = opensearchResults.results.map(
				convertOpenSearchToVideo,
			);
			setSearchResults(convertedVideos);

			// Always update total results from the search results
			setTotalResults(opensearchResults.total || 0);

			// 🚀 ASYNC: Generate search results summary in background (non-blocking)
			generateSearchResultsSummary(convertedVideos);
		}
	}, [opensearchResults]);

	// Pagination logic - now using server-side pagination
	const totalPages = Math.ceil(totalResults / itemsPerPage);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		// Fetch new results for the selected page using current applied data
		if (searchTerm && appliedData?.filter_data) {
			const filterData = appliedData.filter_data;
			handleHybridSearch(
				{
					semantic_query: searchTerm,
					keyword_query: filterData.keywords?.join(', '),
					entity_query: filterData.brands?.join(', '),
					date_range: filterData.date_range,
					analysis_date_range: filterData.analysis_date_range,
					platform: filterData.platform,
					language: filterData.language,
					reach: filterData.reach,
					sentiment: filterData.sentiment,
					entity_logic: filterData.entity_logic,
				},
				page,
			);
		}
		// Scroll to top of results
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// Generate search results summary and Q&A answer (ASYNC - non-blocking)
	const generateSearchResultsSummary = async (results: Video[]) => {
		if (results.length === 0) {
			setQaResultsSummary('');
			setQaAnswer('');
			setIsAnswerLoading(false);
			setIsResultsLoading(false);
			return;
		}

		// Video cards will render immediately, summary and answer will appear when ready
		// Pass the raw OpenSearch results instead of converted Video objects
		const rawOpenSearchResults = opensearchResults?.results || [];

		// 🚀 PRIORITIZE: Generate Q&A answer first, then Results summary
		// Start Q&A answer immediately
		opensearchApi
			.generateSearchResultsQaAnswer(
				rawOpenSearchResults.slice(0, 10),
				userQuestion,
				150,
			)
			.then((qaData) => {
				setQaAnswer(qaData.answer);
				setIsAnswerLoading(false);
			})
			.catch((error) => {
				console.error('❌ Q&A answer generation failed:', error);
				setQaError(
					error instanceof Error
						? error.message
						: 'Failed to generate Q&A answer',
				);
				setIsAnswerLoading(false);
			});

		// Start Results summary immediately after (don't wait for Q&A to finish)
		opensearchApi
			.generateSearchResultsSummary(rawOpenSearchResults.slice(0, 10), 150)
			.then((summaryData) => {
				setQaResultsSummary(summaryData.summary);
				setIsResultsLoading(false);
			})
			.catch((error) => {
				console.error('❌ Results summary generation failed:', error);
				setQaError(
					error instanceof Error
						? error.message
						: 'Failed to generate results summary',
				);
				setIsResultsLoading(false);
			});
	};

	// Summary Content handlers
	const handleGenerateSummary = async () => {
		if (!item?.name) return;

		setSummaryLoading(true);
		setSummaryError('');

		try {
			// Map category name to correct index name
			const categoryToIndexMap: Record<string, string> = {
				'AI Influencers (US)': 'ai-influencers-us-transcripts',
				'Tech influencers (US)': 'tech-influencers-transcripts',
				'Politics (US)': 'politics-us-transcripts',
				'US news': 'us-news-transcripts',
				'Generalist (North America)': 'generalist-north-america-transcripts',
				Skincare: 'skincare-transcripts',
			};

			const indexName =
				categoryToIndexMap[item.name] || 'ai-influencers-us-transcripts';
			const result = await opensearchApi.generateIndexOverview(
				indexName,
				100,
				1000,
			);
			setSummaryContent(result.overview);
		} catch (error) {
			console.error('Summary generation failed:', error);
			setSummaryError(
				error instanceof Error ? error.message : 'Failed to generate summary',
			);
		} finally {
			setSummaryLoading(false);
		}
	};

	useEffect(() => {
		setPageTitle('Video Intelligence Panel');
		setIsCreditsCounterVisible(false);

		// Check OpenSearch API health on component mount
		checkHealth();

		return () => {
			setPageTitle('');
			setIsCreditsCounterVisible(true);
		};
	}, [checkHealth]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.datalake)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	if (isLoading || isFetching) {
		return (
			<Flex
				style={{ width: '100%', height: '300px' }}
				justify="center"
				align="center"
			>
				<Spin />
			</Flex>
		);
	}

	if (!item) {
		return (
			<Flex style={{ width: '100%' }} justify="space-between">
				<Typography.Text style={{ margin: 0 }}>
					Category not found
				</Typography.Text>

				<Button
					onClick={() => navigate(-1)}

					// onClick={() => navigate('/app/dashboard/video-intelligence-lake')}
				>
					<ArrowLeftOutlined /> Back to categories
				</Button>
			</Flex>
		);
	}

	return (
		<>
			<Flex
				vertical
				gap={16}
				style={{ width: '100%', paddingBottom: '2.5rem' }}
				align="flex-start"
				justify="flex-start"
			>
				<Flex style={{ width: '100%' }} justify="space-between">
					<Typography.Title level={3} style={{ margin: 0 }}>
						{item.name}
					</Typography.Title>

					<Flex gap={12} align="center">
						<Button onClick={() => setIsPanelOpen((prev) => !prev)}>
							{isPanelOpen ? 'Hide' : 'Show'} Q&A panel
						</Button>

						<Button
							onClick={() => {
								const lsPage = localStorage.getItem('category_page') || 1;
								navigate(
									`/app/dashboard/video-intelligence-lake?page=${lsPage}`,
								);
							}}
						>
							<ArrowLeftOutlined /> Back
						</Button>
					</Flex>
				</Flex>

				<Flex
					gap={16}
					style={{ width: '100%', justifyContent: 'space-between' }}
					align="stretch"
				>
					{/* Left side - Category info and filters (natural width) */}
					<Flex vertical gap={16}>
						<Flex
							gap={16}
							justify="flex-start"
							align="flex-start"
							style={{ width: '100%' }}
						>
							<div className={styles.imageWrapper}>
								{item?.image_url ? (
									<img
										alt="example"
										src={prepareImageSrc(item?.image_url)}
										width={250}
										height={'auto'}
										onError={(e) => {
											e.currentTarget.src = placeholderImg;
										}}
									/>
								) : (
									<img alt="Category" src={placeholderImg} />
								)}
							</div>

							<Flex vertical gap={16}>
								<Flex vertical gap={8}>
									<Typography.Text>{item.description}</Typography.Text>

									<Typography.Text>
										Update frequency:{' '}
										{UPDATE_FREQUENCY[
											item.update_frequency as keyof typeof UPDATE_FREQUENCY
										] ?? 'Manually'}
									</Typography.Text>
								</Flex>
								{/* <Descriptions
									bordered
									size={'small'}
									column={1}
								>
									<Descriptions.Item label="Description">
										{item.description}
									</Descriptions.Item>
									<Descriptions.Item label="Creators">
										{formatNumber(Number(item.creators_amount) || 0)}
									</Descriptions.Item>
									<Descriptions.Item label="Content">
										{formatNumber(Number(item.video_quantity) || 0)} videos /{' '}
										{formatSecondsToTime(Number(item.video_duration || 0))}
									</Descriptions.Item>

									<Descriptions.Item label="Update frequency">
										{UPDATE_FREQUENCY[
											item.update_frequency as keyof typeof UPDATE_FREQUENCY
										] ?? 'Manually'}
									</Descriptions.Item>

									<Descriptions.Item label="Videos added on last check">
										{Math.floor(Math.random() * 600)}
									</Descriptions.Item>
								</Descriptions> */}

								{/* <Typography.Paragraph style={{ margin: 0 }}>
									{item.description}
								</Typography.Paragraph> */}

								<Flex wrap gap={16}>
									<Card variant="borderless" size="small">
										<Statistic
											title="Creators"
											value={formatNumber(Number(item.creators_amount) || 0)}
											prefix={<StarOutlined />}
											loading={isLoading}
										/>
									</Card>
									<Card variant="borderless" size="small">
										<Statistic
											title="Videos"
											value={`${formatNumber(
												Number(item.video_quantity) || 0,
											)}`}
											// value={`${formatNumber(
											// 	Number(item.video_quantity) || 0,
											// )} videos / ${formatSecondsToTime(
											// 	Number(item.video_duration || 0),
											// )}`}
											prefix={<PlayCircleOutlined />}
											loading={isLoading}
										/>
									</Card>
									<Card variant="borderless" size="small">
										<Statistic
											title="Content duration"
											value={`${formatSecondsToTime(
												Number(item.video_duration || 0),
											)}`}
											prefix={<ClockCircleOutlined />}
											loading={isLoading}
										/>
									</Card>
									<Card variant="borderless" size="small">
										<Statistic
											title="Video added on last check"
											value={Math.floor(
												Math.random() * Number(item?.video_quantity || 0),
											)}
											prefix={<FileDoneOutlined />}
											loading={isLoading}
										/>
									</Card>
								</Flex>
							</Flex>
						</Flex>
						<FiltersSection
							key={appliedData?.id || 'filters-section-component'}
							isOpen={isPanelOpen}
							data={appliedData}
							clearData={() => setAppliedData(null)}
							onFiltersApplied={() => setIsFiltersApplied(true)}
							onSearch={handleSearch}
							onHybridSearch={handleHybridSearch}
						/>
					</Flex>

					{/* Right side - Sidebar charts (takes remaining space) */}
					<SidebarCharts
						summaryContent={summaryContent}
						summaryLoading={summaryLoading}
						summaryError={summaryError}
						onGenerateSummary={handleGenerateSummary}
					/>
				</Flex>

				{/* Q&A Results Component - Show only when search results are available */}
				{isFiltersApplied && (
					<Flex vertical gap={16} style={{ width: '100%' }}>
						<QAResults
							isAnswerLoading={isAnswerLoading}
							isResultsLoading={isResultsLoading}
							answer={qaAnswer}
							resultsSummary={qaResultsSummary}
							error={qaError || undefined}
						/>
					</Flex>
				)}

				{/* Video Cards Section - Show when filters applied */}
				{isFiltersApplied && (
					<Flex vertical gap={16} style={{ width: '100%' }}>
						{/* Pagination Controls */}
						{totalResults > 0 && totalPages > 1 && (
							<Flex
								justify="flex-end"
								align="center"
								style={{ marginBottom: '16px' }}
							>
								<Typography.Text
									type="secondary"
									style={{ marginRight: '16px' }}
								>
									{totalResults} videos
								</Typography.Text>
								<Pagination
									current={currentPage}
									total={totalResults}
									pageSize={itemsPerPage}
									showSizeChanger={false}
									showQuickJumper={false}
									showTotal={(total, range) =>
										`${range[0]}-${range[1]} of ${total}`
									}
									onChange={handlePageChange}
									size="small"
									disabled={opensearchLoading}
								/>
							</Flex>
						)}

						{/* Error Display */}
						{opensearchError && (
							<Flex
								justify="center"
								align="center"
								style={{ marginBottom: '16px' }}
							>
								<Typography.Text type="danger">
									Error: {opensearchError}
								</Typography.Text>
							</Flex>
						)}

						{opensearchLoading ? (
							<Flex
								justify="center"
								align="center"
								style={{ minHeight: '200px' }}
							>
								<Spin size="large" />
							</Flex>
						) : searchResults.length > 0 ? (
							searchResults.map((video) => (
								<VideoCard
									key={video._id}
									video={video}
									searchTerm={searchTerm}
									searchEntities={searchEntities}
								/>
							))
						) : (
							<Flex
								justify="center"
								align="center"
								style={{ minHeight: '200px' }}
							>
								<Typography.Text type="secondary">
									No results found for "{searchTerm}"
								</Typography.Text>
							</Flex>
						)}
					</Flex>
				)}

				{/* Entities Section - Always visible */}
				{outerEntities && outerEntities?.length > 0 ? (
					<Flex vertical gap={8}>
						<Typography.Title level={5} style={{ margin: 0 }}>
							Entities
						</Typography.Title>
						<Tabs
							items={outerEntities
								?.sort((a, b) => a.type.localeCompare(b.type))
								?.map((outerEntity) => ({
									key: outerEntity?.type,
									label: (
										<Flex vertical>
											<Tooltip
												title={
													ENTITIES_TYPES[outerEntity?.type! as TEntityType]
														?.description ??
													ENTITIES_TYPES[outerEntity?.type! as TEntityType]
														?.label
												}
											>
												<Typography.Text
												// style={{ color: '#1677ff' }}
												>
													{ENTITIES_TYPES[outerEntity?.type! as TEntityType]
														?.label ?? outerEntity?.type}
												</Typography.Text>
											</Tooltip>
											{/* <Typography.Text style={{ fontSize: 10, color: '#888' }}>
												{ENTITIES_TYPES[outerEntity?.type! as TEntityType]
													?.description ?? ''}
											</Typography.Text> */}
										</Flex>
									),
									children: (
										<Flex wrap gap={12}>
											{outerEntity.entities.map((entity) => (
												<Card
													key={entity?.name}
													variant="borderless"
													size="small"
												>
													<Statistic
														title={entity?.name}
														value={entity?.count}
														style={{ minWidth: 60 }}
													/>
												</Card>
											))}
										</Flex>
									),
								}))}
						></Tabs>
					</Flex>
				) : (
					<Flex vertical>
						<Typography.Title level={5} style={{ margin: 0 }}>
							Entities
						</Typography.Title>
						Not found
					</Flex>
				)}

				<Creators id={id!} />
			</Flex>
		</>
	);
}
